/**
 * One Worker on the machine-first Workers board: what it is, what it runs, what it is doing.
 *
 * This component owns no Worker or terminal lifecycle. Its one action is onOpenTerminal,
 * which only asks the board to present the Worker's existing terminal. Callers supply facts
 * they actually have; absent optional facts are omitted rather than guessed.
 *
 * State and assignment are separate chips on purpose: an Idle Worker with no project is
 * Idle and Unassigned, never one merged status.
 */
import type { ReactElement, ReactNode } from 'react';
import type { WorkerWorkloadFacts } from '../lib/worker-workload';
import type { WorkerCardAiStatus } from '../lib/ai-execution-profile';
import {
  WORKER_BOARD_STATE_LABEL,
  type WorkerAssignment,
  type WorkerBoardState,
} from '../lib/worker-board';

export interface CollapsedWorkerAttention {
  readonly label: string;
  readonly count?: number;
}

export interface CollapsedWorkerCardProps {
  readonly workerNumber: number;
  readonly workerName: string;
  /** The platform's own status word, shown beside the board state it was read as. */
  readonly status: string;
  readonly state: WorkerBoardState;
  readonly assignment: WorkerAssignment;
  /** Current task or other recorded activity, when one exists. */
  readonly activity?: string | null;
  readonly project?: string | null;
  readonly model?: string | null;
  readonly runtime?: string | null;
  /** The computer's name. The board omits it when the group heading already says it. */
  readonly machine?: string | null;
  /** The terminal's folder (usually the worktree), shortened, with the full path as a title. */
  readonly folder?: string | null;
  readonly folderPath?: string | null;
  /** Already-formatted elapsed time. The card does not infer a start time. */
  readonly elapsed?: string | null;
  /** Recorded current phase. Omitted when the caller has none. */
  readonly phase?: string | null;
  /** The department the current run is working as. Omitted when the run names none. */
  readonly department?: string | null;
  readonly workload?: WorkerWorkloadFacts | null;
  readonly attention?: CollapsedWorkerAttention | null;
  /**
   * #1133: this Worker's own terminal is asking the owner something. The card marks it
   * "Waiting for you" and its one action opens the Worker on that terminal.
   */
  readonly terminalWaiting?: boolean;
  /** A note that the configured AI and the one actually running differ. */
  readonly aiMismatch?: boolean;
  /** #250 what AI this Worker is running, Auto or pinned, and what Brunel recommends. */
  readonly aiStatus?: WorkerCardAiStatus | null;
  /** #250 the Worker's AI selector (the same control as Settings > AI Workforce). */
  readonly aiControl?: ReactNode;
  /** Visual status dot; the textual state stays visible beside it. */
  readonly statusIndicator?: ReactNode;
  readonly terminalId?: string;
  readonly terminalOpen?: boolean;
  readonly onOpenTerminal: () => void;
}

function present(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function workerStatusText(status: string): string {
  const readable = status.trim().replaceAll('_', ' ').replaceAll('-', ' ');
  return readable || 'status unavailable';
}

/** #1133 what a card says first while its Worker's own terminal is asking the owner something. */
export const WORKER_WAITING_FOR_YOU = 'Waiting for you';

export function CollapsedWorkerCard({
  workerNumber,
  workerName,
  status,
  state,
  assignment,
  activity,
  project,
  model,
  runtime,
  machine,
  folder,
  folderPath,
  elapsed,
  phase,
  department,
  workload,
  attention,
  terminalWaiting = false,
  aiMismatch = false,
  aiStatus = null,
  aiControl,
  statusIndicator,
  terminalId,
  terminalOpen = false,
  onOpenTerminal,
}: CollapsedWorkerCardProps): ReactElement {
  const shownActivity = present(activity);
  const shownProject = present(project);
  const shownModel = present(model);
  const shownRuntime = present(runtime);
  const shownMachine = present(machine);
  const shownFolder = present(folder);
  const shownElapsed = present(elapsed);
  // A terminal asking the owner something is what the Worker is on, whatever else was recorded.
  const shownPhase = terminalWaiting ? WORKER_WAITING_FOR_YOU : present(phase);
  const shownDepartment = present(department);
  const readableStatus = workerStatusText(status);
  const stateLabel = WORKER_BOARD_STATE_LABEL[state];
  const shownAttention = attention === null || attention === undefined ? null : present(attention.label);
  const attentionText = shownAttention === null
    ? null
    : attention?.count === undefined ? shownAttention : `${shownAttention} · ${attention.count}`;
  const ai = [shownRuntime, shownModel].filter((part): part is string => part !== null).join(' · ');
  const unassigned = assignment === 'unassigned';
  // The raw status is shown only when it says more than the state chip already does.
  const rawStatusDiffers = readableStatus.toLowerCase() !== stateLabel.toLowerCase()
    && readableStatus.toLowerCase() !== shownAttention?.toLowerCase();

  const accessible = [
    `Worker ${workerNumber} ${workerName}`,
    stateLabel,
    unassigned ? 'Unassigned' : null,
    shownPhase === null ? null : `phase ${shownPhase}`,
    shownDepartment === null ? null : `department ${shownDepartment}`,
    shownActivity === null ? null : `task ${shownActivity}`,
    attentionText,
  ].filter((part): part is string => part !== null).join('. ');

  return (
    <article className={`worker-card state-${state}${unassigned ? ' is-unassigned' : ''}`}
      aria-label={accessible} data-worker-state={state} data-worker-assignment={assignment}>
      <header className="worker-card-head">
        <span className="worker-no" aria-hidden="true">{workerNumber}</span>
        <span className="worker-card-identity">
          <strong className="worker-card-name">{workerName}</strong>
          {ai !== '' && <span className="worker-card-ai" title={ai}>{ai}</span>}
        </span>
        <span className="worker-card-state">
          {statusIndicator}
          <span className={`worker-chip chip-${state}`}>{stateLabel}</span>
        </span>
      </header>

      <div className="worker-card-body">
        {shownPhase !== null && (
          <p className={`worker-phase${terminalWaiting ? ' is-waiting-for-you' : ''}`} data-worker-phase={shownPhase}
            title={terminalWaiting ? 'This Worker\'s terminal is asking you something. Open its Terminal to answer.' : undefined}>
            {shownPhase}
          </p>
        )}
        {shownActivity !== null
          ? <p className="worker-card-task" title={shownActivity}>{shownActivity}</p>
          : <p className="worker-card-task is-empty">No current task</p>}
        <div className="worker-card-tags">
          {shownDepartment !== null && (
            <span className="worker-tag is-department" data-worker-department={shownDepartment}
              title={`Working as the ${shownDepartment} department for this job`}>
              {shownDepartment}
            </span>
          )}
          {unassigned
            ? <span className="worker-chip chip-unassigned">Unassigned</span>
            : shownProject !== null && <span className="worker-tag" title={`Project ${shownProject}`}>{shownProject}</span>}
          {shownFolder !== null && (
            <span className="worker-tag is-mono" title={present(folderPath) ?? shownFolder}>{shownFolder}</span>
          )}
          {shownMachine !== null && <span className="worker-tag">{shownMachine}</span>}
          {rawStatusDiffers && <span className="worker-tag is-quiet">{readableStatus}</span>}
        </div>
        {attentionText !== null && <p className="worker-card-alert" role="note">{attentionText}</p>}
        {aiMismatch && <p className="worker-card-alert">Configured AI differs from the one running</p>}
        {aiStatus !== null && (
          <div className="worker-card-ai-status" data-ai-mode={aiStatus.mode.toLowerCase().replaceAll(' ', '_')}
            data-ai-recommended={aiStatus.recommended}>
            <small data-ai-line="using" className={aiStatus.usingKnown ? undefined : 'muted'}>
              <strong>Using:</strong> {aiStatus.using} <span className="worker-tag">{aiStatus.mode}</span>
            </small>
            <small data-ai-line="recommended" className={aiStatus.recommended === 'none' ? 'muted' : undefined}>
              {aiStatus.recommendation}
            </small>
            {aiStatus.blocked !== null && <small className="worker-card-alert" data-ai-blocked="true">{aiStatus.blocked}</small>}
          </div>
        )}
        {aiControl}
      </div>

      <footer className="worker-card-foot">
        <span className="worker-card-meta">
          {shownElapsed !== null && <small>Elapsed {shownElapsed}</small>}
          {workload != null && (
            <small className="worker-workload" title={`${workload.finished} finished, ${workload.succeeded} succeeded, ${workload.failed} failed`}>
              {workload.finished} recent · {workload.successPercent}% ok
            </small>
          )}
        </span>
        {/* Opens the Worker's surface, whose first view is its read-only run log. The
            interactive terminal is a debug view there, off by default - except while it is
            asking the owner something, when the surface opens on it and this says so. */}
        <button type="button" className={`worker-terminal-action${terminalWaiting ? ' is-waiting-for-you' : ''}`}
          aria-label={terminalWaiting
            ? `Open terminal for Worker ${workerNumber} ${workerName}, waiting for you`
            : `Open run log for Worker ${workerNumber} ${workerName}`}
          aria-haspopup="dialog" aria-expanded={terminalOpen} aria-controls={terminalId}
          onClick={onOpenTerminal}>
          <span aria-hidden="true" className="worker-terminal-glyph">{terminalWaiting ? '>_' : '≡'}</span>
          {terminalWaiting ? 'Terminal' : 'Run log'}
        </button>
      </footer>
    </article>
  );
}
